/**
 * Shared logging utility for SmartSites frontend
 *
 * Provides conditional debug logging based on SMARTSITES_DEBUG flag.
 * Errors are always logged regardless of debug mode.
 */

const LOG_PREFIX = '[SmartSites]';

/**
 * Logs debug messages when SMARTSITES_DEBUG is enabled
 *
 * @param context - The component/module name for log identification
 * @param args - Arguments to log
 */
export function logDebug(context: string, ...args: unknown[]): void {
    if (window.SMARTSITES_DEBUG === true) {
        console.log(`${LOG_PREFIX}[${context}]`, ...args);
    }
}

/**
 * Logs error messages (always logged regardless of debug mode)
 *
 * @param context - The component/module name for log identification
 * @param args - Arguments to log
 */
export function logError(context: string, ...args: unknown[]): void {
    console.error(`${LOG_PREFIX}[${context}]`, ...args);
}

/**
 * Logs warning messages when SMARTSITES_DEBUG is enabled
 *
 * @param context - The component/module name for log identification
 * @param args - Arguments to log
 */
export function logWarn(context: string, ...args: unknown[]): void {
    if (window.SMARTSITES_DEBUG === true) {
        console.warn(`${LOG_PREFIX}[${context}]`, ...args);
    }
}

/**
 * Creates a logger instance bound to a specific context
 *
 * @param context - The component/module name for log identification
 * @returns Object with debug, error, and warn methods
 */
export function createLogger(context: string): {
  debug: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
} {
    return {
        debug: (...args: unknown[]): void => logDebug(context, ...args),
        error: (...args: unknown[]): void => logError(context, ...args),
        warn: (...args: unknown[]): void => logWarn(context, ...args),
    };
}
